/**
 * Wishlist — LocalStorage-based saved products list.
 *
 * Storage shape:
 *   localStorage['lt-wishlist'] = JSON array of objects:
 *     [{ handle, title, vendor, url, image, price, addedAt }, ...]
 *
 * Public API on window.LaTiendaWishlist:
 *   - get(): array
 *   - has(handle): boolean
 *   - add(product): adds, fires 'wishlist:changed' event
 *   - remove(handle): removes, fires event
 *   - toggle(product): add or remove, fires event
 *   - count(): number
 *
 * Vanilla JS, no dependencies. Same patterns as the racket-quiz module.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'lt-wishlist';
  var EVENT_NAME = 'wishlist:changed';

  function readStorage() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function writeStorage(items) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      // Quota exceeded or localStorage disabled — silently fail
    }
  }

  function dispatch() {
    document.dispatchEvent(new CustomEvent(EVENT_NAME, {
      detail: { count: readStorage().length, items: readStorage() }
    }));
  }

  var Wishlist = {
    get: function () { return readStorage(); },
    count: function () { return readStorage().length; },
    has: function (handle) {
      if (!handle) return false;
      return readStorage().some(function (item) { return item.handle === handle; });
    },
    add: function (product) {
      if (!product || !product.handle) return;
      var items = readStorage();
      if (items.some(function (item) { return item.handle === product.handle; })) return;
      items.unshift({
        handle: product.handle,
        title: product.title || '',
        vendor: product.vendor || '',
        url: product.url || ('/products/' + product.handle),
        image: product.image || '',
        price: parseFloat(product.price) || 0,
        compare_at_price: parseFloat(product.compare_at_price) || 0,
        addedAt: Date.now()
      });
      writeStorage(items);
      dispatch();
    },
    remove: function (handle) {
      if (!handle) return;
      var items = readStorage().filter(function (item) { return item.handle !== handle; });
      writeStorage(items);
      dispatch();
    },
    toggle: function (product) {
      if (!product || !product.handle) return false;
      if (Wishlist.has(product.handle)) {
        Wishlist.remove(product.handle);
        return false;
      } else {
        Wishlist.add(product);
        return true;
      }
    },
    clear: function () {
      writeStorage([]);
      dispatch();
    }
  };

  window.LaTiendaWishlist = Wishlist;

  /* ----- DOM wiring ----- */

  function updateBadges() {
    var n = Wishlist.count();
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = n;
      if (n > 0) {
        el.removeAttribute('hidden');
      } else {
        el.setAttribute('hidden', '');
      }
    });
  }

  function updateButtons() {
    document.querySelectorAll('[data-wishlist-button]').forEach(function (btn) {
      var handle = btn.dataset.productHandle;
      var inList = Wishlist.has(handle);
      btn.classList.toggle('wishlist-button--active', inList);
      btn.setAttribute('aria-pressed', inList ? 'true' : 'false');
      btn.setAttribute('aria-label', inList ? 'Remove from wishlist' : 'Add to wishlist');
    });
  }

  function bindButtons() {
    document.querySelectorAll('[data-wishlist-button]').forEach(function (btn) {
      if (btn.__lt_wishlist_bound) return;
      btn.__lt_wishlist_bound = true;
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var product = {
          handle: btn.dataset.productHandle,
          title: btn.dataset.productTitle,
          vendor: btn.dataset.productVendor,
          url: btn.dataset.productUrl,
          image: btn.dataset.productImage,
          price: btn.dataset.productPrice,
          compare_at_price: btn.dataset.productComparePrice
        };
        var added = Wishlist.toggle(product);

        // Pulse animation on click — restart by removing/re-adding class
        btn.classList.remove('wishlist-button--pulse');
        // Force reflow so the animation restarts cleanly
        void btn.offsetWidth;
        btn.classList.add('wishlist-button--pulse');

        // Show a brief toast confirmation
        showToast(added, product.title);
      });
    });
  }

  /* ----- Toast confirmation ----- */

  var toastEl = null;
  var toastTimer = null;

  function ensureToast() {
    if (toastEl) return toastEl;
    toastEl = document.createElement('div');
    toastEl.className = 'wishlist-toast';
    toastEl.setAttribute('role', 'status');
    toastEl.setAttribute('aria-live', 'polite');
    document.body.appendChild(toastEl);
    return toastEl;
  }

  function showToast(added, title) {
    var el = ensureToast();
    el.classList.toggle('wishlist-toast--removed', !added);
    var iconAdded = '<svg class="wishlist-toast__icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">'
      + '<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>'
      + '</svg>';
    var iconRemoved = '<svg class="wishlist-toast__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
      + '<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>'
      + '</svg>';
    var msg = added ? 'Added to wishlist' : 'Removed from wishlist';
    el.innerHTML = (added ? iconAdded : iconRemoved) + '<span>' + msg + '</span>';

    // Trigger animation by toggling visible class
    void el.offsetWidth;
    el.classList.add('wishlist-toast--visible');

    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      el.classList.remove('wishlist-toast--visible');
    }, 2000);
  }

  document.addEventListener(EVENT_NAME, function () {
    updateBadges();
    updateButtons();
  });

  function init() {
    bindButtons();
    updateBadges();
    updateButtons();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-bind when product cards are added dynamically (e.g. collection filter, lazy load)
  if ('MutationObserver' in window) {
    var observer = new MutationObserver(function (mutations) {
      var hasNew = false;
      for (var i = 0; i < mutations.length; i++) {
        for (var j = 0; j < mutations[i].addedNodes.length; j++) {
          var node = mutations[i].addedNodes[j];
          if (node.nodeType !== 1) continue;
          if ((node.matches && node.matches('[data-wishlist-button]')) ||
              (node.querySelector && node.querySelector('[data-wishlist-button]'))) {
            hasNew = true;
            break;
          }
        }
        if (hasNew) break;
      }
      if (hasNew) {
        bindButtons();
        updateButtons();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }
})();
